<script lang="ts">
	import { Section } from "#lib/web-ui/primitives/index.js";
	import type { SectionTheme } from "#lib/web-ui/primitives/index.js";
	import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left";
	import CalendarIcon from "@lucide/svelte/icons/calendar";
	import UserIcon from "@lucide/svelte/icons/user";
	import type { BlogPostArticleProps } from "./blog.types";

	let {
		post,
		siteName,
		siteUrl,
		blogListUrl = "/media/blog",
		theme = "light" as SectionTheme
	}: BlogPostArticleProps & { theme?: SectionTheme } = $props();

	const publicTags = $derived(post.tags ?? []);

	function formatDate(dateStr: string): string {
		return new Date(dateStr).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric"
		});
	}
</script>

<svelte:head>
	<title>{post.title} | {siteName}</title>
	<meta name="description" content={post.excerpt} />
	<link rel="canonical" href="{siteUrl}{blogListUrl}/{post.slug}" />
	<meta property="og:title" content={post.title} />
	<meta property="og:description" content={post.excerpt} />
	<meta property="og:url" content="{siteUrl}{blogListUrl}/{post.slug}" />
	<meta property="og:type" content="article" />
	{#if post.featureImage}
		<meta property="og:image" content={post.featureImage} />
	{/if}
	<meta property="article:published_time" content={post.publishedAt} />
	{#if post.updatedAt}
		<meta property="article:modified_time" content={post.updatedAt} />
	{/if}

	{@html `<script type="application/ld+json">${JSON.stringify({
		"@context": "https://schema.org",
		"@type": "BlogPosting",
		headline: post.title,
		description: post.excerpt,
		datePublished: post.publishedAt,
		...(post.updatedAt && { dateModified: post.updatedAt }),
		...(post.featureImage && { image: post.featureImage }),
		author: { "@type": "Person", name: post.author },
		publisher: { "@type": "Organization", name: siteName, url: siteUrl },
		mainEntityOfPage: { "@type": "WebPage", "@id": `${siteUrl}${blogListUrl}/${post.slug}` }
	})}</script>`}
</svelte:head>

<Section {theme}>
	<article class="mx-auto max-w-3xl">
		<header class="mb-8">
			<a
				href={blogListUrl}
				class="text-muted-foreground hover:text-foreground mb-6 inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
			>
				<ArrowLeftIcon class="h-3.5 w-3.5" />
				Back to articles
			</a>

			<h1 class="text-foreground mb-3 text-3xl leading-tight font-bold md:text-4xl">
				{post.title}
			</h1>

			<div class="flex items-center gap-3 text-sm">
				<span class="text-muted-foreground inline-flex items-center gap-1.5">
					<UserIcon class="h-3.5 w-3.5" />
					{post.author}
				</span>
				<span class="text-muted-foreground">·</span>
				<span class="text-muted-foreground inline-flex items-center gap-1.5">
					<CalendarIcon class="h-3.5 w-3.5" />
					{formatDate(post.publishedAt)}
				</span>
			</div>
		</header>

		{#if post.featureImage}
			<img
				src={post.featureImage}
				alt={post.title}
				class="mb-8 w-full rounded-lg object-cover"
				width="800"
				height="450"
				loading="eager"
			/>
		{/if}

		<div class="blog-content text-foreground mb-12">
			{@html post.html}
		</div>

		<footer class="border-border border-t pt-8">
			{#if publicTags.length > 0}
				<div class="mb-8">
					<h3 class="text-foreground mb-3 text-sm font-semibold tracking-wide uppercase">
						Tags
					</h3>
					<div class="flex flex-wrap gap-2">
						{#each publicTags as tag}
							<span
								class="bg-muted text-foreground inline-block rounded-full px-4 py-2 text-sm font-medium"
							>
								{tag.name}
							</span>
						{/each}
					</div>
				</div>
			{/if}

			<nav>
				<a
					href={blogListUrl}
					class="text-primary hover:text-primary/80 inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
				>
					<ArrowLeftIcon class="h-3.5 w-3.5" />
					All articles
				</a>
			</nav>
		</footer>
	</article>
</Section>
